/**
 * Iter51 Etapa 17 — Produse și Oferte (refactorizat)
 *
 * Schimbări:
 *   - Tab „Tipuri Produse" devine read-only informativ cu cele 6 valori
 *     `product_kind` hardcoded (EE, Gaz, VAS, HVAS, Combo EE+VAS, Combo Gaz+VAS).
 *   - Tab „Oferte" devine DB-backed, cu fiecare ofertă tip-ată direct prin
 *     `product_kind` (fără ProductType intermediar).
 *   - Banner sus: ofertele sunt auto-detectate la importurile SF.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import {
  Plus, Tag, Edit, Trash2, Info, Layers, Sparkles, Zap, Flame, Hash, Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// ===== Iter51 Etapa 14 — Product kind catalog (single source of truth) =====
const PRODUCT_KINDS = [
  { value: "EE",                 label: "EE",              full: "Energie Electrică",       desc: "Furnizare energie electrică standard",      badge: "bg-amber-100 text-amber-800 border-amber-200",   Icon: Zap },
  { value: "Gaz",                label: "Gaz",             full: "Gaz Natural",             desc: "Furnizare gaz natural",                    badge: "bg-orange-100 text-orange-800 border-orange-200", Icon: Flame },
  { value: "VAS",                label: "VAS",             full: "Valoare Adăugată Servicii", desc: "Serviciu adițional (asigurări, mentenanță)", badge: "bg-indigo-100 text-indigo-800 border-indigo-200", Icon: Sparkles },
  { value: "HVAS",               label: "HVAS",            full: "High-Value Added Services", desc: "VAS premium / pachet extins",              badge: "bg-purple-100 text-purple-800 border-purple-200", Icon: Sparkles },
  { value: "Combo EE + VAS",     label: "Combo EE + VAS",  full: "Pachet EE + VAS",         desc: "Energie electrică împreună cu serviciu VAS", badge: "bg-gradient-to-r from-amber-100 to-indigo-100 text-amber-900 border-amber-200",  Icon: Layers },
  { value: "Combo Gaz + VAS",    label: "Combo Gaz + VAS", full: "Pachet Gaz + VAS",        desc: "Gaz natural împreună cu serviciu VAS",     badge: "bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-900 border-orange-200", Icon: Layers },
];
const PRODUCT_KIND_BY_VAL = Object.fromEntries(PRODUCT_KINDS.map((k) => [k.value, k]));
// Iter52 P14h — normalize legacy lowercase/snake_case values from DB.
const _KIND_ALIAS = { ee: "EE", gaz: "Gaz", vas: "VAS", hvas: "HVAS", combo_ee_vas: "Combo EE + VAS", combo_gaz_vas: "Combo Gaz + VAS" };
const normalizeProductKind = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (PRODUCT_KIND_BY_VAL[s]) return s;
  return _KIND_ALIAS[s.toLowerCase()] || s;
};

const Products = () => {
  const { canManageProducts } = useAuth();
  const isAdmin = canManageProducts && canManageProducts();
  const [activeTab, setActiveTab] = useState("kinds");

  // Offers state
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [search, setSearch] = useState("");
  const [filterKind, setFilterKind] = useState("__all__");

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", description: "", product_kind: "", price_per_kwh: "", price: "", active: true });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // Iter52 P14g — bulk multiselect for assigning a product_kind to multiple offers at once
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkKind, setBulkKind] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchOffers = async () => {
    setLoadingOffers(true);
    try {
      const r = await axios.get(`${API_URL}/offers`);
      setOffers(r.data || []);
    } catch (e) {
      toast.error("Eroare la încărcarea ofertelor");
    } finally {
      setLoadingOffers(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const resetForm = () => {
    setForm({ name: "", code: "", description: "", product_kind: "", price_per_kwh: "", price: "", active: true });
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (offer) => {
    setForm({
      name: offer.name || "",
      code: offer.code || "",
      description: offer.description || "",
      product_kind: offer.product_kind || "",
      price_per_kwh: offer.price_per_kwh?.toString() || "",
      price: offer.price?.toString() || "",
      active: offer.active !== false,
    });
    setEditingId(offer.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error("Nume și cod sunt obligatorii"); return; }
    if (!form.product_kind) { toast.error("Tip produs este obligatoriu"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        description: form.description.trim() || null,
        product_kind: form.product_kind,
        price_per_kwh: form.price_per_kwh ? parseFloat(form.price_per_kwh) : null,
        price: form.price ? parseFloat(form.price) : null,
        active: form.active,
      };
      if (editingId) {
        await axios.put(`${API_URL}/offers/${editingId}`, payload);
        toast.success("Ofertă actualizată");
      } else {
        await axios.post(`${API_URL}/offers`, payload);
        toast.success("Ofertă creată");
      }
      setDialogOpen(false);
      resetForm();
      fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/offers/${deleteId}`);
      toast.success("Ofertă dezactivată");
      setDeleteId(null);
      fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  // Filter offers
  const filteredOffers = offers.filter((o) => {
    const norm = normalizeProductKind(o.product_kind);
    if (filterKind === "__unmapped__") {
      if (norm) return false;
    } else if (filterKind !== "__all__" && norm !== filterKind) {
      return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (o.name || "").toLowerCase().includes(q) || (o.code || "").toLowerCase().includes(q);
    }
    return true;
  });

  // Iter52 P14g — bulk assign helpers
  const unmappedCount = offers.filter((o) => !o.product_kind).length;
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const allSelected = filteredOffers.every((o) => prev.has(o.id));
      const next = new Set(prev);
      if (allSelected) {
        filteredOffers.forEach((o) => next.delete(o.id));
      } else {
        filteredOffers.forEach((o) => next.add(o.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const handleBulkAssign = async () => {
    if (!bulkKind || selectedIds.size === 0) return;
    setBulkSaving(true);
    let ok = 0, fail = 0;
    for (const id of selectedIds) {
      try {
        const offer = offers.find((o) => o.id === id);
        if (!offer) continue;
        await axios.put(`${API_URL}/offers/${id}`, { ...offer, product_kind: bulkKind });
        ok++;
      } catch (_e) {
        fail++;
      }
    }
    setBulkSaving(false);
    if (ok) toast.success(`${ok} ofertă/e actualizate (Tip = ${bulkKind})`);
    if (fail) toast.error(`${fail} ofertă/e nu au putut fi actualizate`);
    clearSelection();
    setBulkKind("");
    fetchOffers();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="products-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Produse și Oferte</h1>
        <p className="text-sm text-slate-500 mt-1">Tipurile de produse sunt fixe (catalog standard); ofertele sunt detectate automat din importuri și pot fi validate/editate manual.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="products-tabs">
          <TabsTrigger value="kinds" data-testid="tab-kinds">
            <Layers className="h-3.5 w-3.5 mr-1.5" /> Tipuri Produse ({PRODUCT_KINDS.length})
          </TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers">
            <Tag className="h-3.5 w-3.5 mr-1.5" /> Oferte ({offers.length})
          </TabsTrigger>
        </TabsList>

        {/* ============== TAB 1: PRODUCT KINDS (read-only) ============== */}
        <TabsContent value="kinds" className="mt-4">
          <Card className="border-blue-200 bg-blue-50/50 mb-4" data-testid="kinds-info">
            <CardContent className="p-3 flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Info className="h-4 w-4 text-blue-700" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-blue-900">Catalog standard de tipuri produse</p>
                <p className="text-blue-800 mt-0.5">Cele 6 tipuri sunt hardcoded și folosite atât în clasificarea vânzărilor (câmpul „Tip produs" în Detalii contract) cât și în catalogul de oferte și statistici dashboard. Nu pot fi modificate manual — sunt definitorii pentru întregul sistem.</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kinds-grid">
            {PRODUCT_KINDS.map((k) => (
              <Card key={k.value} className="hover:shadow-md transition-shadow" data-testid={`kind-card-${k.value}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Badge className={`${k.badge} border text-xs font-semibold`}>{k.label}</Badge>
                    <k.Icon className="h-4 w-4 text-slate-400" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{k.full}</p>
                  <p className="text-xs text-slate-500 mt-1">{k.desc}</p>
                  <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{offers.filter((o) => o.product_kind === k.value).length} ofertă/e</span>
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => { setFilterKind(k.value); setActiveTab("offers"); }} data-testid={`kind-card-${k.value}-view-offers`}>
                      Vezi oferte →
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ============== TAB 2: OFFERS (DB-backed CRUD) ============== */}
        <TabsContent value="offers" className="mt-4">
          <Card className="border-emerald-200 bg-emerald-50/50 mb-4" data-testid="offers-info">
            <CardContent className="p-3 flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-4 w-4 text-emerald-700" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-emerald-900">Oferte auto-detectate din importuri</p>
                <p className="text-emerald-800 mt-0.5">La fiecare import Salesforce/Easy, ofertele necunoscute sunt propuse în <code className="bg-emerald-100 px-1 rounded">/admin/proposals</code> sau create automat (dacă flag-ul „Permite crearea ofertelor noi" este activ). Aici le poți valida, edita pricing-ul și asigna tipul de produs corect.</p>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-3">
            <Input
              placeholder="Caută ofertă după nume sau cod..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
              data-testid="offers-search"
            />
            <Select value={filterKind} onValueChange={setFilterKind}>
              <SelectTrigger className="w-[240px]" data-testid="offers-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toate tipurile ({offers.length})</SelectItem>
                <SelectItem value="__unmapped__">⚠️ Neclasificate ({unmappedCount})</SelectItem>
                {PRODUCT_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Button onClick={openCreate} className="ml-auto" data-testid="create-offer-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Ofertă Nouă
              </Button>
            )}
          </div>

          {/* List */}
          {loadingOffers ? (
            <div className="text-center py-12 text-slate-500" data-testid="offers-loading">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Se încarcă...
            </div>
          ) : filteredOffers.length === 0 ? (
            <Card className="border-dashed border-slate-300" data-testid="offers-empty">
              <CardContent className="p-12 text-center">
                <Tag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-base font-semibold text-slate-700">Nicio ofertă încă</p>
                <p className="text-sm text-slate-500 mt-1">
                  {offers.length === 0
                    ? "Catalogul de oferte se va popula automat la primul import Salesforce."
                    : "Nu există rezultate pentru filtrele aplicate. Modifică căutarea sau tipul."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="offers-list">
              {/* Iter52 P14g — bulk multiselect toolbar (admin/BO only) */}
              {isAdmin && filteredOffers.length > 0 && (
                <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="bulk-toolbar">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={filteredOffers.length > 0 && filteredOffers.every((o) => selectedIds.has(o.id))}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid="bulk-select-all"
                    />
                    <span className="font-medium">
                      {selectedIds.size > 0 ? `${selectedIds.size} selectate` : `Selectează toate (${filteredOffers.length})`}
                    </span>
                  </label>
                  {selectedIds.size > 0 && (
                    <>
                      <div className="text-slate-300">|</div>
                      <span className="text-sm text-slate-600">Asignează categorie:</span>
                      <Select value={bulkKind} onValueChange={setBulkKind}>
                        <SelectTrigger className="w-[220px] h-9" data-testid="bulk-kind-select"><SelectValue placeholder="Alege tip produs..." /></SelectTrigger>
                        <SelectContent>
                          {PRODUCT_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={handleBulkAssign}
                        disabled={!bulkKind || bulkSaving}
                        data-testid="bulk-apply-btn"
                      >
                        {bulkSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                        Aplică pe {selectedIds.size}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">
                        Anulează
                      </Button>
                    </>
                  )}
                </div>
              )}
              {filteredOffers.map((o) => {
                const k = PRODUCT_KIND_BY_VAL[normalizeProductKind(o.product_kind)];
                const isSelected = selectedIds.has(o.id);
                return (
                  <Card key={o.id} className={`hover:shadow-sm transition-shadow ${isSelected ? "ring-2 ring-sky-300" : ""}`} data-testid={`offer-row-${o.id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      {isAdmin && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectOne(o.id)}
                          className="h-4 w-4 rounded border-slate-300"
                          data-testid={`offer-select-${o.id}`}
                          aria-label={`Selectează ${o.name}`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-900 truncate">{o.name}</p>
                          <Badge variant="outline" className="text-[11px] font-mono">{o.code}</Badge>
                          {k ? (
                            <Badge className={`${k.badge} border text-[11px]`}>{k.label}</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[11px]" data-testid={`offer-unmapped-${o.id}`}>
                              ⚠️ Neclasificată
                            </Badge>
                          )}
                          {!o.active && <Badge className="bg-slate-200 text-slate-700 text-[11px]">Inactiv</Badge>}
                        </div>
                        {o.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{o.description}</p>}
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                          {o.price_per_kwh != null && <span>{o.price_per_kwh} RON/kWh</span>}
                          {o.price != null && <span>{o.price} RON</span>}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-offer-${o.id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteId(o.id)} data-testid={`delete-offer-${o.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ============== Offer Create/Edit Dialog ============== */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editează ofertă" : "Ofertă nouă"}</DialogTitle>
            <DialogDescription>Completează detaliile ofertei. Tipul de produs este obligatoriu.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nume <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="offer-form-name" placeholder="ex. PPC Simplu Senior" />
            </div>
            <div className="space-y-1.5">
              <Label>Cod <span className="text-red-500">*</span></Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="offer-form-code" placeholder="ex. PPC-SIMPLU-SENIOR" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Tip produs <span className="text-red-500">*</span></Label>
              <Select value={form.product_kind} onValueChange={(v) => setForm({ ...form, product_kind: v })}>
                <SelectTrigger data-testid="offer-form-product-kind"><SelectValue placeholder="Alege tip..." /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.full}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Descriere</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="offer-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Preț per kWh (RON)</Label>
                <Input type="number" step="0.01" value={form.price_per_kwh} onChange={(e) => setForm({ ...form, price_per_kwh: e.target.value })} data-testid="offer-form-price-per-kwh" />
              </div>
              <div className="space-y-1.5">
                <Label>Preț fix (RON)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="offer-form-price" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Activ</Label>
              <Switch checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} data-testid="offer-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Anulează</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="offer-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {editingId ? "Salvează" : "Creează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent data-testid="offer-delete-dialog">
          <DialogHeader>
            <DialogTitle>Confirmă dezactivare</DialogTitle>
            <DialogDescription>Oferta va fi marcată inactivă (soft-delete) pentru a păstra istoricul contractelor existente.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Anulează</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="offer-delete-confirm">Dezactivează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
